import styled, { css } from 'styled-components';
import Button from '../button/button.component';
import { BREAKPOINTS } from '../../constants/breakpoints';
import { WAVEFORM_BAR_HEIGHTS, WAVEFORM_LOTTIE_VIEW_BOX } from './constants';

// Full-screen recording must cover app-level floating controls, including LiveChat
// (1,118,000) and mobile sidebars (10,000,000). Toasts intentionally remain above it.
const RECORDING_OVERLAY_Z_INDEX = 10_000_001;

interface WarningProps {
  $isWarning?: boolean;
}

export const Overlay = styled.dialog<{ $isProcessing?: boolean }>`
  position: fixed;
  inset: 0;
  z-index: ${RECORDING_OVERLAY_Z_INDEX};
  display: flex;
  justify-content: center;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  border: 0;
  overflow-y: auto;
  background-color: ${({ $isProcessing }) =>
    $isProcessing ? 'rgba(244, 248, 255, 0.8)' : 'rgba(0, 22, 41, 0.95)'};
  backdrop-filter: blur(2px);
  font-family: ${({ theme }) => theme.fonts.body.fontFamily};
`;

export const ModalContent = styled.div`
  display: flex;
  width: 42rem;
  max-width: calc(100% - 3.2rem);
  flex-direction: column;
  align-items: center;
  gap: 4rem;
  padding: 20rem 0 4rem;

  @media (max-width: ${BREAKPOINTS.mobile.css}) {
    gap: 2.4rem;
    padding-top: 8rem;
  }
`;

export const ModalTitle = styled.h2`
  width: 100%;
  margin: 0;
  color: ${({ theme }) => theme.colors.namedColors.white};
  font-size: 2.4rem;
  font-weight: 700;
  line-height: 3rem;
  letter-spacing: 0.5px;
  text-align: center;
`;

export const RecordingSection = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 1.2rem;
`;

export const Waveform = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 2rem 0;
`;

export const WaveBar = styled.div<WarningProps & { $height: number }>`
  width: 1rem;
  height: ${({ $height }) => $height / 10}rem;
  border-radius: 10rem;
  background-color: ${({ theme, $isWarning }) =>
    $isWarning
      ? theme.colors.baseColors.red100
      : theme.colors.baseColors.blue100};
`;

const LOTTIE_GROUP_HEIGHT = Math.max(...WAVEFORM_BAR_HEIGHTS);
const LOTTIE_GROUP_WIDTH =
  (LOTTIE_GROUP_HEIGHT * WAVEFORM_LOTTIE_VIEW_BOX.width) /
  WAVEFORM_LOTTIE_VIEW_BOX.height;

export const LottieBarGroup = styled.div`
  width: ${LOTTIE_GROUP_WIDTH / 10}rem;
  height: ${LOTTIE_GROUP_HEIGHT / 10}rem;
  flex: 0 0 auto;

  > div,
  svg {
    display: block;
    width: 100%;
    height: 100%;
  }
`;

export const TimerRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 1.2rem;
  padding-bottom: 2rem;
  letter-spacing: 3px;
  white-space: nowrap;
`;

export const TimerElapsed = styled.span<WarningProps>`
  color: ${({ theme, $isWarning }) =>
    $isWarning
      ? theme.colors.baseColors.red100
      : theme.colors.namedColors.white};
  font-size: 6rem;
  font-weight: 600;
  line-height: 7rem;
`;

export const TimerLimit = styled.span`
  color: ${({ theme }) => theme.colors.baseColors.grey65};
  font-size: 1.8rem;
  font-weight: 500;
  line-height: 2.4rem;
`;

export const CheatSheet = styled.div<WarningProps>`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 0.4rem;
  padding: 0.8rem 1.6rem;
  border: 1px solid
    ${({ theme, $isWarning }) =>
      $isWarning
        ? theme.colors.baseColors.red100
        : theme.colors.baseColors.blue100};
  border-radius: 1rem;
  background-color: ${({ theme, $isWarning }) =>
    $isWarning ? theme.colors.baseColors.red5 : '#e5f3ff'};
`;

export const CheatSheetHeader = styled.div<WarningProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  color: ${({ theme, $isWarning }) =>
    $isWarning
      ? theme.colors.baseColors.red80
      : theme.colors.baseColors.blue100};

  svg {
    width: 1.6rem;
    height: 1.6rem;
  }
`;

export const CheatSheetLabel = styled.span<WarningProps>`
  color: ${({ theme, $isWarning }) =>
    $isWarning
      ? theme.colors.baseColors.red80
      : theme.colors.baseColors.blue100};
  font-size: 1.2rem;
  font-weight: 500;
  line-height: 1.6rem;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

export const CheatSheetText = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.baseColors.grey150};
  font-size: 1.4rem;
  font-style: italic;
  font-weight: 400;
  line-height: 2rem;
  letter-spacing: 0.5px;
  text-align: center;
`;

export const Actions = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  gap: 2.4rem;
`;

const actionButtonBase = css`
  height: 4.8rem;
  padding: 1.2rem 1.6rem;
  border-radius: 1rem;
  font-size: 1.6rem;
  line-height: 2.2rem;
  letter-spacing: 0.5px;
  white-space: nowrap;

  svg {
    width: 2.4rem;
    height: 2.4rem;
    fill: currentColor;
  }
`;

export const CancelButton = styled(Button)`
  ${actionButtonBase};
  flex-shrink: 0;
  border-color: transparent;
  background-color: ${({ theme }) => theme.colors.namedColors.white};
  color: ${({ theme }) => theme.colors.baseColors.blue100};

  @media (max-width: ${BREAKPOINTS.mobile.css}) {
    width: 4.8rem;
    padding: 1.2rem;

    .cancel-label {
      display: none;
    }
  }
`;

export const RecordButton = styled(Button)<{ $isRecording?: boolean }>`
  ${actionButtonBase};
  flex: 1 0 0;
  min-width: 0;
  border-color: transparent;
  background-color: ${({ theme, $isRecording }) =>
    $isRecording
      ? theme.colors.baseColors.red80
      : theme.colors.baseColors.blue100};

  @media (hover: hover) and (pointer: fine) {
    &:hover:not(:disabled) {
      border-color: transparent;
      background-color: ${({ theme, $isRecording }) =>
        $isRecording
          ? theme.colors.baseColors.red100
          : theme.colors.baseColors.blue80};
    }
  }

  &:active:not(:disabled) {
    border-color: transparent;
    background-color: ${({ theme, $isRecording }) =>
      $isRecording
        ? theme.colors.baseColors.red150
        : theme.colors.baseColors.blue150};
  }
`;

export const ProcessingContent = styled.div`
  display: flex;
  width: 45.6rem;
  max-width: calc(100% - 3.2rem);
  min-height: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2.4rem;
`;

export const ProcessingSpinner = styled.div`
  width: 4rem;
  height: 4rem;
  border: 3px solid ${({ theme }) => theme.colors.namedColors.primary};
  border-top-color: transparent;
  border-right-color: transparent;
  border-radius: 50%;
  animation: voice-processing-spin 1s ease-in-out infinite;

  @keyframes voice-processing-spin {
    to {
      transform: rotate(360deg);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 2s;
  }
`;

export const ProcessingTexts = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 0.4rem;
  color: ${({ theme }) => theme.colors.baseColors.grey150};
  letter-spacing: 0.5px;
  text-align: center;
`;

export const ProcessingTitle = styled.h2`
  margin: 0;
  font-size: 2.4rem;
  font-weight: 700;
  line-height: 3rem;
`;

export const ProcessingDescription = styled.p`
  margin: 0;
  font-size: 1.4rem;
  font-weight: 500;
  line-height: 2rem;
`;
